import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Clock, Loader2, Plus, Search } from 'lucide-react';
import type { SystemRole } from '@peles/shared';
import { useGlobalSearch } from '@speles7172/search-console';
import { highlightParts, searchTerms } from '@speles7172/search-client/core';
import { EntityModal } from '../common/EntityModal';
import { globalSearchTransport } from '../../api/search';
import { useRecentEvents } from '../../hooks/useRecentEvents';
import { NEW_EVENT_PATH, goTargetsFor } from './shortcuts';
import { searchTypeIcon } from './searchTypes';

/** Results per kind of record at first; "Show more" doubles it. */
export const PALETTE_RESULTS_PER_TYPE = 5;
/** The engine's `maxLimit` — the most "Show more" can ask for. */
const PALETTE_MAX_RESULTS = 50;
const DEBOUNCE_MS = 200;
/** The engine refuses shorter text. */
const MIN_LENGTH = 2;

export interface PaletteItem {
  id: string;
  group: string;
  label: string;
  /** The label with the searched words marked; plain `label` otherwise. */
  title?: ReactNode;
  subtitle?: string;
  icon: ReactNode;
  /** Where choosing it goes… */
  to?: string;
  /** …or what choosing it does (Show more). */
  run?: () => void;
}

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  return (
    <>
      {highlightParts(text, terms).map((part, i) =>
        part.match ? (
          <mark key={i} className="bg-transparent text-primary-700 font-semibold">
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  );
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  role: SystemRole | undefined;
}

/**
 * Quick search (⌘K / Ctrl+K, or /). Empty, it offers the recently opened
 * events, "New event" and the pages; typed into, it runs the global search
 * (`GET /search?q=` through `useGlobalSearch`) — every kind of record this
 * person may search, narrowed by the type chips, each kind with "Show more" —
 * alongside those, and Enter opens the highlighted one. Arrow keys move,
 * Escape closes.
 */
export function CommandPalette({ open, onClose, role }: CommandPaletteProps) {
  return (
    <EntityModal open={open} onClose={onClose} title="Search" size="sm">
      {/* Mounted per opening, so each search starts empty. */}
      {open && <PaletteBody onClose={onClose} role={role} />}
    </EntityModal>
  );
}

function PaletteBody({ onClose, role }: { onClose: () => void; role: SystemRole | undefined }) {
  const navigate = useNavigate();
  const { events: recent } = useRecentEvents();
  const search = useGlobalSearch(globalSearchTransport, {
    debounceMs: DEBOUNCE_MS,
    minLength: MIN_LENGTH,
    limit: PALETTE_RESULTS_PER_TYPE,
    maxLimit: PALETTE_MAX_RESULTS,
  });
  const { text, setText, response, stale, loading, error, ready } = search;
  // The highlight follows a record, not a row number, so a refetch that
  // reorders the results never moves Enter onto a different record.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = text.trim();
  const terms = useMemo(() => searchTerms(trimmed), [trimmed]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // A search that failed is not a search that found nothing.
  const failed = !loading && ready && error !== null;
  // ⚠️ Only an answer for the text on screen: while a newer search is on its
  // way the old hits are not offered, so Enter never opens one of them.
  const answer = response && !stale ? response : null;
  const failedGroups = answer?.groups.filter((g) => g.error !== undefined) ?? [];

  const items = useMemo<PaletteItem[]>(() => {
    const actions: PaletteItem[] = [
      {
        id: 'action:new-event',
        group: 'Actions',
        label: 'New event',
        icon: <Plus size={16} />,
        to: NEW_EVENT_PATH,
      },
    ];
    const pages: PaletteItem[] = goTargetsFor(role).map((t) => ({
      id: `page:${t.to}`,
      group: 'Go to',
      label: t.label,
      subtitle: `G then ${t.key.toUpperCase()}`,
      icon: <ArrowRight size={16} />,
      to: t.to,
    }));

    if (!trimmed) {
      const recentItems: PaletteItem[] = recent.map((e) => ({
        id: `recent:${e.id}`,
        group: 'Recent events',
        label: e.name,
        icon: <Clock size={16} />,
        to: `/events/${e.id}`,
      }));
      return [...recentItems, ...actions, ...pages];
    }

    const found: PaletteItem[] = [];
    for (const group of answer?.groups ?? []) {
      for (const hit of group.hits) {
        found.push({
          id: `${group.type}:${hit.id}`,
          group: group.label,
          label: hit.title,
          title: <Highlighted text={hit.title} terms={terms} />,
          subtitle: hit.subtitle ?? undefined,
          icon: searchTypeIcon(group.type),
          to: hit.url,
        });
      }
      if (group.more && search.canShowMore) {
        const lastHit = group.hits.at(-1);
        found.push({
          id: `more:${group.type}`,
          group: group.label,
          label: `Show more ${group.label.toLowerCase()}`,
          icon: <ChevronDown size={16} />,
          run: () => {
            search.showMore();
            // Stay where the list was, rather than jumping back to the top.
            if (lastHit) setActiveKey(`${group.type}:${lastHit.id}`);
          },
        });
      }
    }
    return [
      ...found,
      ...actions.filter((a) => matches(a.label, trimmed)),
      ...pages.filter((p) => matches(p.label, trimmed)),
    ];
  }, [trimmed, answer, terms, recent, role, search.canShowMore, search.showMore]);

  // A new term starts from the top; otherwise the highlighted record keeps
  // it, falling back to the top if that record has gone.
  useEffect(() => {
    setActiveKey(null);
  }, [trimmed]);
  const index = items.findIndex((item) => item.id === activeKey);
  const active = index === -1 ? 0 : index;

  // …and in view when the arrow keys take it past the edge of the list.
  useEffect(() => {
    document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, listId]);

  const choose = (item: PaletteItem | undefined) => {
    if (!item) return;
    if (item.run) {
      item.run();
      // A click on Show more must not take the keys away from the search box.
      inputRef.current?.focus();
      return;
    }
    onClose();
    if (item.to) navigate(item.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (items.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveKey(items[(active + step + items.length) % items.length].id);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(items[active]);
    }
  };

  const toggleType = (type: string) =>
    search.setTypes(
      search.types.includes(type)
        ? search.types.filter((t) => t !== type)
        : [...search.types, type],
    );

  const activeId = items[active] ? `${listId}-${active}` : undefined;
  let lastGroup = '';
  const searching = trimmed.length > 0 && ready && loading;

  return (
    <div className="-mx-1">
      <div className="flex items-center gap-2 px-3 h-11 rounded-xl border border-gray-200 bg-gray-50 focus-within:border-primary-400 focus-within:bg-white">
        <Search size={16} className="text-gray-400 shrink-0" aria-hidden />
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search events, guests, vendors, tasks, files…"
          role="combobox"
          aria-label="Search"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none"
        />
        {searching && (
          <Loader2 size={16} className="animate-spin text-gray-400" aria-label="Searching" />
        )}
      </div>

      {search.availableTypes.length > 1 && (
        <div role="group" aria-label="Search in" className="mt-2 flex flex-wrap gap-1.5 px-1">
          <TypeChip
            label="All"
            pressed={search.types.length === 0}
            onClick={() => search.setTypes([])}
          />
          {search.availableTypes.map((t) => (
            <TypeChip
              key={t.type}
              label={t.label}
              icon={searchTypeIcon(t.type)}
              pressed={search.types.includes(t.type)}
              onClick={() => toggleType(t.type)}
            />
          ))}
        </div>
      )}

      <ul
        id={listId}
        role="listbox"
        aria-label="Results"
        className="mt-3 max-h-[60vh] overflow-auto"
      >
        {items.map((item, i) => {
          const header = item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const isActive = i === active;
          return (
            <li key={item.id} role="presentation">
              {header && (
                <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {header}
                </div>
              )}
              <div
                id={`${listId}-${i}`}
                role="option"
                aria-selected={isActive}
                aria-label={item.label}
                onMouseMove={() => setActiveKey(item.id)}
                onClick={() => choose(item)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm ${
                  isActive
                    ? 'bg-primary-50 text-primary-800'
                    : item.run
                      ? 'text-primary-700'
                      : 'text-gray-700'
                }`}
              >
                <span className={isActive ? 'text-primary-600' : 'text-gray-400'}>{item.icon}</span>
                <span className="flex-1 min-w-0 truncate font-medium">
                  {item.title ?? item.label}
                </span>
                {item.subtitle && (
                  <span className="text-xs text-gray-400 truncate max-w-[40%]">
                    {item.subtitle}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {failedGroups.length > 0 && (
        <p className="px-3 pt-2 text-xs text-gray-500">
          Could not search {failedGroups.map((g) => g.label.toLowerCase()).join(', ')} just now.
        </p>
      )}
      {failed && (
        <div
          role="alert"
          className="mx-3 mt-2 flex items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          Search could not be completed.
          <button type="button" onClick={search.refresh} className="font-semibold hover:underline">
            Try again
          </button>
        </div>
      )}
      {trimmed && !ready && items.length === 0 && (
        <p className="px-3 py-6 text-center text-sm text-gray-500">
          Type at least {MIN_LENGTH} characters to search records.
        </p>
      )}
      {trimmed && ready && answer && !searching && !failed && items.length === 0 && (
        <p className="px-3 py-6 text-center text-sm text-gray-500">
          Nothing matches &ldquo;{trimmed}&rdquo;.
        </p>
      )}
    </div>
  );
}

function TypeChip({
  label,
  icon,
  pressed,
  onClick,
}: {
  label: string;
  icon?: ReactNode;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium min-h-8 ${
        pressed
          ? 'border-primary-300 bg-primary-50 text-primary-700'
          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
      }`}
    >
      {icon && <span aria-hidden>{icon}</span>}
      {label}
    </button>
  );
}
